import styled from '@emotion/styled';
import { useState } from 'react';
import { transportErrorReason } from '~/modules/RemoteMic/Network/Client/NetworkClient';
import UserMediaEnabled from '~/modules/UserMedia/UserMediaEnabled';
import isDev from '~/modules/utils/isDev';
import Connect from '~/routes/RemoteMic/Panels/Microphone/Connect';
import RemoteMicKeyboard from '~/routes/RemoteMic/Panels/Microphone/Keyboard';
import MicPreview from '~/routes/RemoteMic/Panels/Microphone/MicPreview';
import { ConnectionStatuses } from '~/routes/RemoteMic/RemoteMic';
import usePermissions from '~/routes/RemoteMic/hooks/usePermissions';
import { AutoEnableFullscreenSetting, useSettingValue } from '~/routes/Settings/SettingsState';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
}

function Microphone({ roomId, monitoringStarted, setIsKeepAwakeOn, connectionError, connectionStatus }: Props) {
  const permissions = usePermissions();
  const [searchActive, setSearchActive] = useState(false);
  const [autoEnableFullscreen] = useSettingValue(AutoEnableFullscreenSetting);
  const [triggerPlayerChangeModal, setTriggerPlayerChangeModal] = useState(false);

  const onConnect = async () => {
    setTriggerPlayerChangeModal(true);
    setIsKeepAwakeOn(true);

    try {
      if (!isDev() && autoEnableFullscreen) {
        await document.body.requestFullscreen();
        global.screen.orientation.unlock();
        await global.screen.orientation.lock?.('portrait');
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const isConnected = connectionStatus === 'connected';

  const micPreview = <MicPreview isVisible isMicOn={monitoringStarted} isConnected={isConnected} />;

  return (
    <MicrophoneLayout>
      <UserMediaEnabled
        showImages={false}
        fallback={
          <FallbackPanel>
            {micPreview}
            <FallbackTitle>Please allow access to the microphone.</FallbackTitle>
          </FallbackPanel>
        }>
        <Panel collapse={searchActive}>
          <MicPreview
            isVisible={isConnected}
            isMicOn={monitoringStarted}
            isConnected={isConnected}
            showPlayerChangeModal={triggerPlayerChangeModal}
          />
          <Connect
            roomId={roomId}
            isVisible={true}
            connectionStatus={connectionStatus}
            onConnect={onConnect}
            connectionError={connectionError}
          />
        </Panel>
        <ControlsPanel>
          {permissions === 'write' && <RemoteMicKeyboard onSearchStateChange={setSearchActive} />}
          {permissions === 'read' && (
            <NoPermissionsMsg data-test="no-permissions-message">
              No permission to control the game. Go to in-game <strong>Settings</strong> menu ➔{' '}
              <strong>Remote Microphones Settings</strong> to manage them.
            </NoPermissionsMsg>
          )}
        </ControlsPanel>
      </UserMediaEnabled>
    </MicrophoneLayout>
  );
}
export default Microphone;

const MicrophoneLayout = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  padding: 14px;
  box-sizing: border-box;
`;

const Panel = styled.div<{ collapse?: boolean }>`
  flex: ${(props) => (props.collapse ? '0 0 auto' : '1 1 auto')};
  min-height: ${(props) => (props.collapse ? '0' : '190px')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  opacity: ${(props) => (props.collapse ? 0.38 : 1)};
  transform: translateY(${(props) => (props.collapse ? '-8px' : '0')});
  transition: 220ms ease;

  @media (max-height: 560px) {
    min-height: ${(props) => (props.collapse ? '0' : '150px')};
  }
`;

const ControlsPanel = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FallbackPanel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: stretch;
`;

const FallbackTitle = styled.h2`
  margin: 0;
  color: var(--walky-ink);
  font-size: 18px;
  line-height: 1.2;
  text-align: center;
`;

const NoPermissionsMsg = styled.h5`
  margin: 0;
  padding: 12px;
  color: var(--walky-ink);
  font-size: 14px;
  line-height: 1.35;
  border: 1px solid var(--walky-border);
  border-radius: 10px;
  background: var(--walky-surface-raised);
`;
